import { useNavigate } from 'react-router-dom';
import { AccountMenu, Shell, type ShellNavSection } from '@bcecloud/portal-ui';
import { getUser, logout } from '@/lib/auth';
import { clerkSignOut } from '@/lib/clerk-token';
import { clearViewAsCustomer } from '@/lib/view-as';

const SECTIONS: readonly ShellNavSection[] = [
  {
    label: null,
    items: [{ to: '/events', label: 'My events', icon: '▦' }],
  },
];

export interface MyEventsShellProps {
  readonly children: React.ReactNode;
  /** Pinned under the top bar — the staff "viewing as" notice. */
  readonly banner?: React.ReactNode;
}

/**
 * BCE-branded chrome for the customer event portal — the same shell as
 * app.bcecloud.com and the subscription portals, in BCE's own colors.
 *
 * One navigation entry is not a reason for a different layout: customers also
 * see BCE's other surfaces in emails and links, and identical chrome is what
 * makes them read as one product.
 *
 * @param props - The page to render inside the shell.
 * @returns The application shell.
 */
export function MyEventsShell({ children, banner }: MyEventsShellProps): React.ReactElement {
  const navigate = useNavigate();
  const user = getUser() as
    | { firstName?: string; lastName?: string; email?: string; company?: string }
    | null
    | undefined;

  const name =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'My account';

  return (
    <Shell
      identity={{
        name: 'Best Corporate Events',
        // The white artwork — the navy sidebar would swallow the standard logo.
        logoUrl: '/images/logo-white.png',
        tagline: 'MY EVENTS',
        // No sidebarColor: the Shell falls back to --portal-brand-sidebar,
        // which bce-theme.css pins to BCE navy. A hex here would fork the brand.
      }}
      sections={SECTIONS}
      {...(banner === undefined ? {} : { banner })}
      account={(variant) => (
        <AccountMenu
          name={name}
          email={user?.email ?? ''}
          {...(user?.company ? { standing: user.company } : {})}
          actions={[
            {
              label: 'Sign out',
              onSelect: () => {
                // Both identities, or it is not a sign-out: the sign-in screen
                // returns anyone with a live Clerk session straight to the app.
                void clerkSignOut().finally(() => {
                  clearViewAsCustomer();
                  logout();
                  navigate('/auth/login');
                });
              },
              tone: 'danger',
            },
          ]}
          variant={variant}
        />
      )}
    >
      {children}
    </Shell>
  );
}
